import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import { useStyles } from "./RCAIssueDetail.styles";
import MainTransparentBackButton from "components/MainTransparentBackButton";
import RCATicketNumber from "pages/Corporate/RCA/components/RCATicketNumber";
import { IRCATicketTypeEnum } from "pages/Corporate/RCA/components/RCATicketNumber/RCATicketNumberInterfaces";
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Divider,
    FormHelperText,
    InputLabel,
    LinearProgress,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import MainCardButtonAction from "components/MainCardButtonAction";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import UploadDropZone from "components/UploadDropZone/UploadDropZone.view";
import RCAUploadZone from "pages/Corporate/RCA/components/RCAUploadZone";
import { RCAUploadTypeEnum } from "pages/Corporate/RCA/components/RCAUploadZone/RCAUploadZoneInterfaces";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { createRef, useContext, useMemo, useState, useRef, useEffect } from "react";
import { DropzoneRef } from "react-dropzone";
import {
    CORPORATE_BACKOFFICE_PATHS,
    RCA_STATUSES,
    USERTYPE,
    documentsFileTypeExtensions,
    documentsFileTypeExtensionsRegExp,
    mediaFileTypeExtensions,
    mediaFileTypeExtensionsRegExp,
} from "constants/constants";
import { useTranslation } from "react-i18next";
import RCAStatusDropdown from "pages/Corporate/RCA/components/RCAStatusDropdown";
import { IssueEntity } from "entities/RCA/IssueEntity/IssueEntity";
import { uploadIssueFiles } from "api/queries/RCA/IssueAPI";
import { IRCAIssueDetailFormValues } from "./RCAIssueDetailInterfaces";
import { Form, Formik } from "formik";
import { addRCAIssueDetailFormValidationSchema } from "validations/schemas";
import dayjs from "dayjs";
import useRolesPermissions from "hooks/useRolesPermissions";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import RCAAddObservationModal from "pages/Corporate/RCA/components/RCAAddObservationModal/RCAAddObservationModal.view";
import RCAObservationItem from "pages/Corporate/RCA/components/RCAObservationItem";
import useDateFormat from "hooks/useDateFormat";
import { DocumentPreviewer } from "components/DocumentPreviewer/DocumentPreviewer.view";
import { RCAElipsisMenu } from "pages/Corporate/RCA/components/RCAElipsisMenu/RCAElipsisMenu";
import { RCAMediaPreviewer } from "pages/Corporate/RCA/components/RCAMediaPreviewer/RCAMediaPreviewer";
import moment from "moment";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";
import { IOption } from "pages/Corporate/RCA/components/RCAElipsisMenu/RCAElipsisMenuInterfaces";
import RCACreateEditIssuesModal from "pages/Corporate/RCA/components/RCACreateEditIssuesModal";
import MainModal from "components/MainModal";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import { RCAIssueHistoryLogs } from "./component";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import ActionPlansTable from "pages/Corporate/RCA/components/ActionPlansTable/ActionPlansTable.view";

const RCAIssueDetail = () => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { issueId } = useParams();
    const dropzoneDocumentsRef = createRef<DropzoneRef>();
    const dropzoneMediaRef = createRef<DropzoneRef>();
    const navigate = useNavigate();
    const location = useLocation();
    const [openIssueObservationModal, setOpenIssueObservationModal] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState(false);
    const [openIssueDeleteModal, setOpenIssueDeleteModal] = useState<boolean>(false);
    const [openEditIssueModalOpen, setOpenEditIssueModalOpen] = useState(false);

    const { t } = useTranslation();
    const { getDateFormatByLanguage, formatDate } = useDateFormat();
    const { apolloQueryError, issue, loading, refetch } = IssueEntity.GetIssue(Number(issueId)!);
    const {
        issueLogs,
        loading: loadingLogs,
        refetch: refetchIssueLogs,
    } = IssueEntity.GetIssueHistoricChangesLog(Number(issueId)!);
    const { mills } = RCALibraryEntity.GetMills();
    const { updateIssueMediaMutation } = IssueEntity.UpdateIssueMedia();
    const [progress, setProgress] = useState(0);
    const [isUploadingFiles, setIsUploadingFiles] = useState<RCAUploadTypeEnum | null>(null);
    const { updateIssueMutation } = IssueEntity.UpdateIssue();
    const [snackbarFailureMessage, setSnackbarFailureMessage] = useState("");
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
    const [fileDescription, setFileDescription] = useState("");
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const { currentUser } = useContext(CorporateContext);
    const actionPlansTableRef = useRef<HTMLDivElement>(null);
    const hasScrolledToActionPlansRef = useRef<boolean>(false);

    const rcaOwnershipIds = useMemo(
        () =>
            issue
                ? [
                      ...(issue?.owner?.user_id ? [issue.owner.user_id] : []),
                      ...(issue?.created_by ? [issue?.created_by] : []),
                  ]
                : [],
        [issue],
    );
    const { RCA_PERMISSIONS } = useRolesPermissions(rcaOwnershipIds);

    const initFormValues: IRCAIssueDetailFormValues = {
        dueDate: new Date(issue?.due_date) || new Date(),
        ownerId: issue?.owner?.user_id || "",
        statusId: issue?.status?.status_id || RCA_STATUSES.NOT_STARTED,
        millId: issue?.mill_id,
        meetingTypeId: issue?.meeting_type_id || "",
    };

    const rcaElipsisMenuOptions: IOption[] = useMemo(
        () => [
            {
                name: t("rca.issues.issue_detail.edit_issue"),
                callback: () => setOpenEditIssueModalOpen(true),
                disabled: !RCA_PERMISSIONS?.issues?.canEdit,
            },
            {
                name: t("rca.issues.issue_detail.delete_issue"),
                callback: () => setOpenIssueDeleteModal(true),
                disabled: !RCA_PERMISSIONS?.issues?.canDelete,
            },
        ],
        [RCA_PERMISSIONS, t],
    );

    const documentFiles = useMemo(() => {
        if (!issue?.issue_media) return [];
        return issue.issue_media.filter((file) => documentsFileTypeExtensionsRegExp.test(file.media.media_type));
    }, [issue]);

    const mediaFiles = useMemo(() => {
        if (!issue?.issue_media) return [];
        return issue.issue_media.filter((file) => mediaFileTypeExtensionsRegExp.test(file.media.media_type));
    }, [issue]);

    const editIssueDetails = useMemo(
        () => ({
            issueId: issue.issue_id,
            description: issue.description,
            shortDescription: issue.title,
            keySuccessFactorId: issue.issue_key_success_factor_indicators?.[0]?.key_success_factor_indicator_id,
            millId: issue.mill_id,
            meetingTypeId: issue?.meeting_type_id,
            createdAt: issue.created_at,
            incidentDate: issue?.incident_date,
        }),
        [issue],
    );

    const isAdmin = useMemo(
        () =>
            currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE,
        [currentUser],
    );

    const millOptions = useMemo(() => {
        if (!mills) return [];
        return mills.map((m) => ({
            id: m.mill_id?.toString() || "",
            name: m.name ? m.name : "",
        }));
    }, [mills]);

    const handleFileChange = async (files: File[], mediaType: RCAUploadTypeEnum) => {
        if (!files || (files && !files.length)) return;
        setIsUploadingFiles(mediaType);
        setPendingFiles(files);
        setIsFileUploadModalOpen(true);
    };

    const handleFileUpload = async () => {
        if (!pendingFiles.length) return;
        const fileExtension = pendingFiles[0].name.split(".").pop();
        const renamedFile = new File(
            [pendingFiles[0]],
            fileDescription ? `${fileDescription}.${fileExtension}` : pendingFiles[0].name,
            { type: pendingFiles[0].type },
        );
        const formData = new FormData();
        formData.set("file", renamedFile);
        formData.set("issue_id", issueId!.toString() || "");
        formData.set("user_id", currentUser?.user_id!.toString() || "");

        try {
            await uploadIssueFiles(Number(issueId), formData, (uploadEvt) => {
                let percentCompleted = Math.round((uploadEvt.loaded * 100) / (uploadEvt?.total || 1));
                setProgress(percentCompleted);
            });

            refetch();
            await refetchIssueLogs();
            setProgress(0);
            setIsUploadingFiles(null);
            setSnackbarMessage(t("rca.issues.issue_detail.upload_success"));
        } catch (error) {
            setProgress(0);
            setIsUploadingFiles(null);
        }

        setFileDescription("");
        setIsFileUploadModalOpen(false);
        setPendingFiles([]);
    };

    const handleSubmit = async (values: IRCAIssueDetailFormValues) => {
        setIsSaving(true);
        const issueDetailValues = {
            due_date: values.dueDate.toISOString(),
            status_id: values.statusId,
            owner_id: values.ownerId,
            mill_id: values.millId,
            ...(values.meetingTypeId && { meeting_type_id: values.meetingTypeId }),
        };

        try {
            await updateIssueMutation({
                variables: {
                    issueId: issue?.issue_id,
                    changes: {
                        ...issueDetailValues,
                    },
                },
            });
            refetch();
            await refetchIssueLogs();
            setIsSaving(false);
            setSnackbarMessage(t("rca.issues.issue_detail.update_success"));
        } catch {
            setIsSaving(false);
            return;
        }
    };

    const handleDeleteIssue = async () => {
        const issueDetailValues = {
            deleted_at: moment.utc().toDate(),
        };
        const state = location.state;

        try {
            await updateIssueMutation({
                variables: {
                    issueId: issue?.issue_id,
                    changes: {
                        ...issueDetailValues,
                    },
                },
            });

            if (state?.from) {
                navigate(state?.from);
            } else {
                navigate(
                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}`,
                );
            }
            setIsSaving(false);
        } catch {
            setSnackbarFailureMessage(t("rca.issues.issue_detail.delete_failed"));
            setIsSaving(false);
            return;
        }
    };

    const handleDeleteMediaFile = async (issueMediaId: number) => {
        const issueMediaValues = {
            deleted_at: moment.utc().toDate(),
        };

        try {
            await updateIssueMediaMutation({
                variables: {
                    issueMediaId: issueMediaId,
                    changes: {
                        ...issueMediaValues,
                    },
                },
            });
            await refetch();
            await refetchIssueLogs();

            setSnackbarMessage(t("rca.rca_library.rca_detail.media.delete_success"));
        } catch {
            setSnackbarFailureMessage(t("rca.rca_library.rca_detail.media.delete_failed"));
            return;
        }
    };

    useEffect(() => {
        const isComingFromActionPlan = location.state?.from?.includes(
            `${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}`,
        );

        if (
            isComingFromActionPlan &&
            !loading &&
            issue &&
            actionPlansTableRef.current &&
            !hasScrolledToActionPlansRef.current
        ) {
            hasScrolledToActionPlansRef.current = true;
            setTimeout(() => {
                actionPlansTableRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 10);
        }
    }, [loading, issue, location.state]);

    if ((!issue && !loading) || (issue && !issue.issue_id))
        return (
            <div className={mainClasses.mainWrapper}>
                <div className={`${classes.issueDetailWrapper} ${classes.issueDetailWrapperNoData}`}>
                    <CircularProgress color="secondary" />
                    {t("rca.issues.issue_detail.loading")}
                </div>
            </div>
        );

    if (!issueId || !issue)
        return (
            <div className={mainClasses.mainWrapper}>
                <div className={`${classes.issueDetailWrapper} ${classes.issueDetailWrapperNoData}`}>
                    <ErrorOutlineOutlinedIcon className={classes.noDataIcon} />
                    {t("rca.issues.issue_detail.no_data")}
                </div>
            </div>
        );

    return (
        <div className={mainClasses.mainWrapper}>
            <div className={classes.issueDetailWrapper}>
                <div className={`${classes.leftContainer} ${mainClasses.contentWrapper}`}>
                    <div className={classes.headerActions}>
                        <MainTransparentBackButton
                            onClick={() => {
                                navigate(
                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}`,
                                );
                            }}
                        />
                        <RCAElipsisMenu options={rcaElipsisMenuOptions} />
                    </div>
                    <RCATicketNumber
                        ticketNumber={issue?.display_number || ""}
                        ticketType={IRCATicketTypeEnum.ISSUE}
                        className={classes.ticketNumber}
                    />
                    <div className={classes.issueTitle}>{issue.title}</div>
                    <Divider className={classes.divider} />
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Box className={mainClasses.contentFormBox}>
                            <InputLabel id="outlined-issueDescription-label">
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.issues.issue_detail.description")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={4}
                                type="text"
                                variant="outlined"
                                value={issue.description}
                            />
                        </Box>
                        <Box>
                            <InputLabel id="outlined-issueDescription-label">
                                <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                    {t("rca.issues.issue_detail.incident_date")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                disabled
                                fullWidth
                                className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                multiline
                                rows={4}
                                type="text"
                                variant="outlined"
                                value={formatDate(issue.incident_date || issue.created_at)}
                            />
                        </Box>
                    </div>
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.issues.issue_detail.observations.title")}
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.issues.canEdit}
                                    title={t("rca.issues.issue_detail.observations.add_new_observation")}
                                    renderIcon={() => <AddCircleIcon />}
                                    onClick={() => setOpenIssueObservationModal(true)}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {issue.issue_observations.map((observation) => (
                                <RCAObservationItem observation={observation} key={observation.issue_observation_id} />
                            ))}
                        </div>
                        {openIssueObservationModal && (
                            <RCAAddObservationModal
                                open
                                onClose={() => setOpenIssueObservationModal(false)}
                                onAddObservation={async () => await refetch()}
                                id={Number(issueId)}
                            />
                        )}
                    </Card>
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.issues.issue_detail.documents.title")}
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.issues.canEdit}
                                    title={t("rca.issues.issue_detail.documents.upload_a_file")}
                                    renderIcon={() => <FileUploadOutlinedIcon />}
                                    onClick={() => dropzoneDocumentsRef.current?.open()}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {!!RCA_PERMISSIONS?.issues.canEdit && (
                                <UploadDropZone
                                    onFilesChange={(files) => handleFileChange(files, RCAUploadTypeEnum.DOCUMENT)}
                                    hidePreview
                                    progress={progress}
                                    dropzoneRef={dropzoneDocumentsRef}
                                    showAddedFiles={false}
                                    renderUploadZone={() => <RCAUploadZone uploadType={RCAUploadTypeEnum.DOCUMENT} />}
                                    acceptedFileTypes={documentsFileTypeExtensions}
                                />
                            )}
                            {isUploadingFiles === RCAUploadTypeEnum.DOCUMENT && (
                                <div className={classes.progressWrapper}>
                                    <div>{t("rca.issues.issue_detail.documents.uploading")}</div>
                                    <LinearProgress variant="determinate" value={progress} color="info" />
                                </div>
                            )}
                            {!!documentFiles && documentFiles.length > 0 && (
                                <div className={classes.documentFilesWrapper}>
                                    {documentFiles.map((documentFile) => (
                                        <DocumentPreviewer
                                            document={documentFile}
                                            canDelete={RCA_PERMISSIONS?.issues?.canDelete}
                                            key={documentFile.issue_media_id}
                                            handleDelete={async () =>
                                                await handleDeleteMediaFile(documentFile.issue_media_id)
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                    <Card className={`${mainClasses.contentCard} ${classes.card}`}>
                        <div className={mainClasses.contentCardHeader}>
                            <div className={mainClasses.contentCardTitle}>
                                {t("rca.issues.issue_detail.media.title")}
                            </div>
                            <div className={mainClasses.contentCardActions}>
                                <MainCardButtonAction
                                    disabled={!RCA_PERMISSIONS?.issues.canEdit}
                                    title={t("rca.issues.issue_detail.media.upload_a_photo_or_video")}
                                    renderIcon={() => <FileUploadOutlinedIcon />}
                                    onClick={() => dropzoneMediaRef.current?.open()}
                                />
                            </div>
                        </div>
                        <div className={classes.cardContent}>
                            {!!RCA_PERMISSIONS?.issues.canEdit && (
                                <UploadDropZone
                                    hide={!!issue.issue_media && issue.issue_media.length > 0}
                                    dropzoneRef={dropzoneMediaRef}
                                    onFilesChange={(files) => handleFileChange(files, RCAUploadTypeEnum.MEDIA)}
                                    hidePreview
                                    showAddedFiles={false}
                                    renderUploadZone={() => <RCAUploadZone uploadType={RCAUploadTypeEnum.MEDIA} />}
                                    acceptedFileTypes={mediaFileTypeExtensions}
                                />
                            )}
                            {isUploadingFiles === RCAUploadTypeEnum.MEDIA && (
                                <div className={classes.progressWrapper}>
                                    <div>{t("rca.issues.issue_detail.media.uploading")}</div>
                                    <LinearProgress variant="determinate" value={progress} color="info" />
                                </div>
                            )}
                            {!!mediaFiles && mediaFiles.length > 0 && (
                                <div className={classes.mediaFilesWrapper}>
                                    {mediaFiles.map((mediaFile) => (
                                        <RCAMediaPreviewer
                                            canDelete={RCA_PERMISSIONS?.issues?.canDelete}
                                            mediaFile={mediaFile.media}
                                            key={mediaFile.issue_media_id}
                                            handleDelete={async () =>
                                                await handleDeleteMediaFile(mediaFile.issue_media_id)
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                    <ActionPlansTable
                        ref={actionPlansTableRef}
                        parentType="issue"
                        parentId={issue.issue_id}
                        rows={!loading && !apolloQueryError ? issue.action_plans || [] : []}
                        loading={loading}
                        millId={issue?.mill_id}
                        onActionPlanCreated={async () => {
                            await refetch();
                            await refetchIssueLogs();
                        }}
                    />
                    <RCAIssueHistoryLogs logs={issueLogs} loadingLogs={loadingLogs} />
                </div>

                <div className={classes.rightContainer}>
                    <Formik
                        initialValues={initFormValues}
                        validationSchema={addRCAIssueDetailFormValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue, dirty, errors, touched }) => {
                            return (
                                <Form className={mainClasses.contentForm}>
                                    <RCAStatusDropdown
                                        disabled={isSaving || !RCA_PERMISSIONS?.issues.canEdit}
                                        handleChange={(value) => {
                                            setFieldValue("statusId", value);
                                        }}
                                        value={values.statusId}
                                    />
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-dueDate-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.issues.issue_detail.due_date")}
                                            </Typography>
                                        </InputLabel>

                                        <DatePicker
                                            disablePast
                                            className={`${mainClasses.contentFormDatePicker} ${classes.datePicker}`}
                                            value={dayjs(values.dueDate)}
                                            format={getDateFormatByLanguage()}
                                            disabled={isSaving || !RCA_PERMISSIONS?.issues.canEdit}
                                            onChange={(date) => {
                                                setFieldValue("dueDate", date);
                                            }}
                                        />
                                    </Box>
                                    {isAdmin && (
                                        <>
                                            <Divider className={classes.formItemDivider} />
                                            <Box className={mainClasses.contentFormBox}>
                                                <InputLabel id="outlined-description-label">
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={mainClasses.contentFormLabel}
                                                    >
                                                        {t("rca.issues.add_issue.mill")}
                                                    </Typography>
                                                </InputLabel>
                                                <NewMainSelect
                                                    disabled={isSaving || !RCA_PERMISSIONS?.issues.canEdit}
                                                    fullWidth
                                                    name="millId"
                                                    options={millOptions || []}
                                                    placeholder={t("rca.issues.add_issue.mill_placeholder")}
                                                    onChange={(value) => {
                                                        setFieldValue("millId", value);
                                                        setFieldValue("ownerId", "");
                                                    }}
                                                    value={values.millId && String(values.millId)}
                                                />
                                            </Box>
                                        </>
                                    )}
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-owner-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.issues.issue_detail.owner")}
                                            </Typography>
                                        </InputLabel>
                                        <RCAUsersDropdown
                                            millId={values.millId}
                                            fullWidth
                                            disabled={isSaving || !RCA_PERMISSIONS?.action_plans.canEdit}
                                            name="ownerId"
                                            placeholder={t("rca.settings.owners_dropdown.select_owner")}
                                            value={values.ownerId}
                                            onChange={(selected) => {
                                                setFieldValue("ownerId", selected);
                                            }}
                                            multiple={false}
                                        />
                                        {!!errors.ownerId && !!touched.ownerId && (
                                            <FormHelperText className={mainClasses.contentFormHelperText}>
                                                {t(errors.ownerId)}
                                            </FormHelperText>
                                        )}
                                    </Box>
                                    <Divider className={classes.formItemDivider} />
                                    <Box className={mainClasses.contentFormBox}>
                                        <InputLabel id="outlined-meetingTypeId-label">
                                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                                {t("rca.rca_library.rca_detail.meeting_type")}
                                            </Typography>
                                        </InputLabel>
                                        <RCAMeetingTypesDropdown
                                            fullWidth
                                            multiple={false}
                                            disabled={isSaving || !RCA_PERMISSIONS?.issues.canEdit}
                                            value={values.meetingTypeId}
                                            onChange={(value) => {
                                                setFieldValue("meetingTypeId", value);
                                            }}
                                        />
                                    </Box>
                                    {issue.created_by && (
                                        <>
                                            <Divider className={classes.formItemDivider} />
                                            <Box className={mainClasses.contentFormBox}>
                                                <InputLabel id="outlined-owner-label">
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={mainClasses.contentFormLabel}
                                                    >
                                                        {t("rca.action_plans.action_plan_details.submitted_by")}
                                                    </Typography>
                                                </InputLabel>
                                                <RCAUsersDropdown
                                                    millId={issue.mill_id}
                                                    fullWidth
                                                    disabled
                                                    name="submittedById"
                                                    placeholder={t(
                                                        "rca.action_plans.action_plan_details.select_submitted_by",
                                                    )}
                                                    value={issue.created_by}
                                                    onChange={(selected) => {}}
                                                    multiple={false}
                                                    className={classes.disabledDropdown}
                                                />
                                            </Box>
                                        </>
                                    )}
                                    <Divider className={classes.formItemDivider} />
                                    <Button
                                        className={mainClasses.contentButton}
                                        variant="contained"
                                        color="info"
                                        type="submit"
                                        disabled={isSaving || !dirty || !RCA_PERMISSIONS?.issues.canEdit}
                                    >
                                        {t("rca.issues.issue_detail.save_changes")}
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                    <Snackbar
                        open={snackbarMessage !== ""}
                        onClose={() => setSnackbarMessage("")}
                        autoHideDuration={6000}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Alert severity="success" sx={{ width: "100%" }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                    <Snackbar
                        open={snackbarFailureMessage !== ""}
                        onClose={() => setSnackbarFailureMessage("")}
                        autoHideDuration={6000}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Alert severity="error" sx={{ width: "100%" }}>
                            {snackbarFailureMessage}
                        </Alert>
                    </Snackbar>
                    <RCAConfirmDeleteModal
                        open={openIssueDeleteModal}
                        onClose={() => setOpenIssueDeleteModal(false)}
                        onDeleteItem={handleDeleteIssue}
                        title={t("rca.issues.issue_delete_modal.title")}
                        warning={t("rca.issues.issue_delete_modal.warning")}
                        confirm={t("rca.issues.issue_delete_modal.confirm")}
                    />
                    <RCACreateEditIssuesModal
                        open={openEditIssueModalOpen}
                        onClose={() => {
                            setOpenEditIssueModalOpen(false);
                        }}
                        onAddIssue={async () => {
                            await refetch();
                            await refetchIssueLogs();
                            setSnackbarMessage(t("rca.issues.issue_detail.update_success"));
                        }}
                        editIssue={editIssueDetails}
                        onError={() => {
                            setOpenEditIssueModalOpen(false);
                            setSnackbarFailureMessage(t("rca.issues.issue_detail.update_failed"));
                        }}
                    />
                    <MainModal
                        isOpen={isFileUploadModalOpen}
                        title={t("upload_files_description_modal.title")}
                        onClose={() => setIsFileUploadModalOpen(false)}
                        onOkButtonClick={() => {
                            setIsFileUploadModalOpen(false);
                            handleFileUpload();
                        }}
                    >
                        <TextField
                            className={mainClasses.contentFormTextFieldModal}
                            type="text"
                            variant="outlined"
                            placeholder={t("upload_files_description_modal.text_field_placeholder")}
                            value={fileDescription}
                            onChange={(e) => setFileDescription(e.target.value)}
                        />
                    </MainModal>
                </div>
            </div>
        </div>
    );
};

export default RCAIssueDetail;
